import { prefersReducedMotion } from '@lucid/core';

class LucidUgc extends HTMLElement {
  #observer = null;
  #active = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('play', this.#onMediaPlay, true);
    this.addEventListener('pause', this.#onMediaPause, true);
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.35 });
      this.querySelectorAll('[data-ugc-tile]').forEach((tile) => this.#observer.observe(tile));
    }
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#pause(this.#active);
  }

  #video(tile) {
    return tile?.querySelector('video') ?? null;
  }

  #setState(tile, playing) {
    const button = tile.querySelector('[data-ugc-play]');
    tile.classList.toggle('is-playing', playing);
    if (!button) return;
    button.setAttribute('aria-pressed', String(playing));
  }

  #play(tile) {
    const video = this.#video(tile);
    if (!video) return;
    if (this.#active && this.#active !== tile) this.#pause(this.#active);
    this.#active = tile;
    video.muted = true;
    video.playsInline = true;
    if (prefersReducedMotion()) video.loop = false;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => this.#setState(tile, false));
    }
  }

  #pause(tile) {
    const video = this.#video(tile);
    if (!tile || !video) return;
    video.pause();
    if (this.#active === tile) this.#active = null;
  }

  #onMediaPlay = (event) => {
    const tile = event.target instanceof HTMLVideoElement ? event.target.closest('[data-ugc-tile]') : null;
    if (!tile) return;
    tile.classList.remove('is-paused');
    this.#setState(tile, true);
  };

  #onMediaPause = (event) => {
    const video = event.target instanceof HTMLVideoElement ? event.target : null;
    const tile = video?.closest('[data-ugc-tile]');
    if (!tile) return;
    if (video.currentTime > 0) tile.classList.add('is-paused');
    this.#setState(tile, false);
  };

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ugc-play]') : null;
    if (!button) return;
    const tile = button.closest('[data-ugc-tile]');
    const video = this.#video(tile);
    if (!tile || !video) return;
    if (video.paused) {
      this.#play(tile);
    } else {
      this.#pause(tile);
    }
  };

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting && !this.#video(entry.target)?.paused) this.#pause(entry.target);
    }
  };

  #onVisibility = () => {
    if (document.visibilityState === 'hidden') this.#pause(this.#active);
  };
}

if (!customElements.get('lucid-ugc')) customElements.define('lucid-ugc', LucidUgc);
